import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import useToggleStateOnUser from 'App/hooks/useToggleStateOnUser';
import './AccordionWrapper.scss';
import TherigyLoadingSpinner from 'App/components/TherigyLoadingSpinner';

const Style = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;

    button {
        background: transparent;
        border: none;
        font-size: 24px;
        font-weight: 500;
        margin-top: 30px;
        margin-bottom: 30px;

        &:hover {
            color: var(--brandPrimary);
            text-decoration: underline;
            cursor: pointer;
        }
    }
`;

const HeaderContentDefault = ({ onClick, title, isVisible, setIsVisible, className }) => (
    <Style className={className}>
        <button
            type="button"
            data-testid="accordion-title"
            onClick={() => {
                setIsVisible(!isVisible);
                onClick?.(!isVisible);
            }}
        >
            <i className={isVisible ? 'fa fa-caret-down' : 'fa fa-caret-right'} style={{ marginRight: '8px' }} />
            {title}
        </button>
    </Style>
);

HeaderContentDefault.propTypes = {
    title: PropTypes.node.isRequired,
    onClick: PropTypes.func,
    isVisible: PropTypes.bool,
    setIsVisible: PropTypes.func,
    className: PropTypes.string,
};

HeaderContentDefault.defaultProps = {
    onClick: null,
    isVisible: false,
    setIsVisible: null,
    className: '',
};

const propTypes = {
    title: PropTypes.node.isRequired,
    ActionElement: PropTypes.element,
    children: PropTypes.node,
    showActionWhenClosed: PropTypes.bool,
    stateKey: PropTypes.string,
    defaultIsVisible: PropTypes.bool,
    // Prop `controlledVisible` is only necessary if you want to externally control the open/closed state
    controlledVisible: PropTypes.bool,
    onClick: PropTypes.func,
    HeaderContent: PropTypes.func,
    style: PropTypes.shape({}),
    headerClassName: PropTypes.string,
    keepMounted: PropTypes.bool,
};

const defaultProps = {
    ActionElement: null,
    children: null,
    showActionWhenClosed: false,
    stateKey: null,
    defaultIsVisible: true,
    controlledVisible: undefined,
    onClick: null,
    HeaderContent: HeaderContentDefault,
    style: null,
    headerClassName: '',
    keepMounted: false,
};

const AccordionWrapper = ({
    title,
    ActionElement,
    children,
    showActionWhenClosed,
    stateKey,
    defaultIsVisible,
    controlledVisible,
    onClick,
    HeaderContent,
    style,
    headerClassName,
    keepMounted,
}) => {
    const sessionKey = stateKey || `Accordion-${title}`;
    const [isVisible, setIsVisible, isLoading] = useToggleStateOnUser(sessionKey, defaultIsVisible);

    useEffect(() => {
        if (typeof controlledVisible === 'boolean' && controlledVisible !== isVisible) {
            setIsVisible(controlledVisible);
        }
    }, [controlledVisible, isVisible, setIsVisible]);

    if (isLoading) {
        return <TherigyLoadingSpinner />;
    }

    return (
        <div className="accordion-wrapper" style={style} data-testid="accordion-wrapper">
            <div className="accordion-title">
                <HeaderContent
                    onClick={onClick}
                    title={title}
                    isVisible={isVisible}
                    setIsVisible={setIsVisible}
                    className={headerClassName}
                />
                {(isVisible || showActionWhenClosed) && ActionElement}
            </div>
            {(isVisible || keepMounted) && (
                <div className={cx('accordion-body', { 'd-none': !isVisible })} data-testid="accordion-body">
                    {children}
                </div>
            )}
        </div>
    );
};

AccordionWrapper.propTypes = propTypes;
AccordionWrapper.defaultProps = defaultProps;

export default AccordionWrapper;
